import { Box, ButtonBase, Divider, Stack, Typography } from '@mui/material';
import { Theme, alpha, styled } from '@mui/material/styles';

import { DashboardTone } from '../model/tones';

const ELEVATION_OPACITY = 0.15;

const elevationColor = (theme: Theme): string =>
  alpha(theme.palette.common.black, ELEVATION_OPACITY);

export type IconTone = 'blue' | 'red';

export const toneColor = (theme: Theme, tone: DashboardTone): string => {
  switch (tone) {
    case 'blue':
      return theme.palette.blue[500];
    case 'darkRed':
      return theme.palette.red[700];
    case 'orange':
      return theme.palette.orange[500];
    case 'green':
      return theme.palette.green[500];
    default:
      return theme.palette.red[500];
  }
};

export const CardRoot = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: '1rem',
  borderRadius: '0.75rem',
  boxShadow: `0px 1px 8px 0px ${elevationColor(theme)}`,
  gap: '1rem',
  height: '100%',
  minWidth: 0,
}));

export const CardIconBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone: IconTone }>(({ theme, tone }) => ({
  width: '2rem',
  height: '2rem',
  borderRadius: '0.5rem',
  backgroundColor: tone === 'blue' ? theme.palette.blue[100] : theme.palette.red[100],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}));

export const CardTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 400,
  lineHeight: 1.25,
  color: theme.palette.grey[900],
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('md')]: { fontSize: '1rem' },
}));

export const BigNumber = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 400,
  lineHeight: 1,
  color: theme.palette.grey[900],
}));

export const BigNumberCaption = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 400,
  lineHeight: 1.25,
  color: theme.palette.grey[900],
}));

const LINK_BUTTON_GAP = '0.25rem';
export const ROW_INLINE_PADDING = '1rem';

export const rowEndPadding = (
  mobilePadding: string = ROW_INLINE_PADDING,
): { xs: string; md: string } => ({
  xs: mobilePadding,
  md: LINK_BUTTON_GAP,
});

export const GrayRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '0.5rem',
  backgroundColor: theme.palette.grey[50],
  borderRadius: '0.75rem',
  minHeight: '2.5625rem',
  paddingLeft: ROW_INLINE_PADDING,
  paddingRight: ROW_INLINE_PADDING,
  minWidth: 0,
}));

export const RowPrimaryText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 400,
  lineHeight: 1.3,
  color: theme.palette.grey[900],
}));

export const RowSecondaryText = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 400,
  lineHeight: 1.25,
  color: theme.palette.grey[900],
}));

export const ChipNote = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 400,
  lineHeight: 1.25,
  color: theme.palette.grey[900],
}));

export const PriorityDot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone: DashboardTone }>(({ theme, tone }) => ({
  width: '0.5rem',
  height: '0.5rem',
  borderRadius: '50%',
  flexShrink: 0,
  backgroundColor: toneColor(theme, tone),
}));

export const RowIconBox = styled(Box)(({ theme }) => ({
  width: '2rem',
  height: '2rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  color: theme.palette.red[500],
}));

export const ArrowButton = styled(ButtonBase)(({ theme }) => ({
  minHeight: '1.6875rem',
  padding: '0.25rem 0.75rem',
  borderRadius: '0.5rem',
  flexShrink: 0,
  '&:hover': { backgroundColor: theme.palette.action.hover },
}));

export const ViewMoreButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'compact',
})<{ compact?: boolean }>(({ theme, compact }) => ({
  alignSelf: 'flex-end',
  marginTop: 'auto',
  fontFamily: '"Neue Frutiger World"',
  fontSize: compact ? '0.75rem' : '0.875rem',
  fontWeight: 400,
  lineHeight: compact ? 1.25 : 1.3,
  gap: compact ? '0.25rem' : 0,
  textAlign: 'center',
  color: theme.palette.grey[900],
  borderRadius: '0.5rem',
  '& svg': { width: '1.25rem', height: '1.25rem', flexShrink: 0 },
  '& svg path': { fill: theme.palette.grey[900] },
}));

export const ViewMoreChevron = styled('span', {
  shouldForwardProp: (prop) => prop !== 'expanded',
})<{ expanded: boolean }>(({ theme, expanded }) => ({
  display: 'inline-flex',
  transform: expanded ? 'rotate(-90deg)' : 'none',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.short,
  }),
}));

export const EmptyText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 400,
  lineHeight: 1.25,
  color: theme.palette.grey[900],
  padding: '0.5rem 0',
}));

export const ErrorText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.red[700],
  padding: '0 0.25rem',
}));

export const HeaderBar = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '0.625rem',
  backgroundColor: theme.palette.background.paper,
  borderRadius: '0.8125rem',
  filter: `drop-shadow(0px 1px 4px ${elevationColor(theme)})`,
  minHeight: '3.375rem',
  padding: '0.4375rem 0.5rem',
}));

export const MutedLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 400,
  lineHeight: 1.25,
  textAlign: 'center',
  color: theme.palette.grey[600],
}));

export const PeriodButton = styled(ButtonBase)(({ theme }) => ({
  justifyContent: 'flex-start',
  gap: '0.5rem',
  width: '20rem',
  height: '2.5rem',
  padding: '0 1rem',
  border: `1px solid ${elevationColor(theme)}`,
  borderRadius: '0.75rem',
  backgroundColor: theme.palette.background.paper,
  [theme.breakpoints.down('sm')]: { width: '100%' },
}));

export const FOUR_UP_COLUMNS = {
  xs: '1fr',
  md: 'repeat(2, minmax(0, 1fr))',
  xl: 'repeat(4, minmax(0, 1fr))',
};

export const TWO_UP_COLUMNS = { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' };

export const DashboardGrid = styled(Box)({
  display: 'grid',
  gap: '1rem',
});

export const ForecastCard = styled(Stack)(({ theme }) => ({
  gap: '0.5rem',
  minWidth: 0,
  padding: '0.75rem',
  borderRadius: '1rem',
  border: `1px solid ${theme.palette.grey[200]}`,
  backgroundColor: theme.palette.background.paper,
}));

export const ForecastTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 400,
  lineHeight: 1.25,
  color: theme.palette.grey[900],
}));

export const ForecastPlace = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 350,
  lineHeight: 1.25,
  color: theme.palette.blue[500],
}));

export const ForecastDivider = styled(Divider)(({ theme }) => ({
  borderColor: theme.palette.grey[100],
  marginTop: '0.25rem',
}));

export const ForecastDayLabel = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 400,
  lineHeight: 1.25,
  color: theme.palette.grey[900],
}));
